import { Component, computed, effect, inject, input, linkedSignal, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute } from '@angular/router';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { EAnalyticsStatus } from '@enums/campaign-analytics.enum';
import { EPlatformFeature } from '@enums/campaign.enum';
import { EEventType } from '@enums/event-tracking.enum';
import { ICampaignAnalyticsApi, ICreatorCtaKpiStatusApi } from '@interfaces/campaign-analytics-api.interface';
import { IAnalyticsNode } from '@interfaces/campaign-analytics.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ICreatorCampaignExport, TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { IconComponent } from '@shared/components/icon/icon.component';
import { toEventFrontendTab, toEventViewMode } from '@utilities/event-payload-mapping.util';
import { SocialUtilitiesService } from '@utilities/social-utilities.service';
import { ICampaignExportApi } from '../campaign-details-banner/campaign-export-dialog/campaign-export-dialog.interface';
import { TDeepLinkEvent } from '../campaign-details-deep-link/campaign-details-deep-link.interface';
import { CampaignDetailsDeliverablesFilterSelectComponent } from '../campaign-details-deliverables/campaign-details-deliverables-filter-select/campaign-details-deliverables-filter-select.component';
import { ICampaignDetailsDeliverablesFilterOption } from '../campaign-details-deliverables/campaign-details-deliverables.interface';
import { ECampaignDetailsTab, ICampaignDetailsCreator } from '../campaign-details.interface';
import { CampaignDetailsCreatorsGridComponent } from './campaign-details-creators-grid/campaign-details-creators-grid.component';
import { CampaignDetailsCreatorsTableComponent } from './campaign-details-creators-table/campaign-details-creators-table.component';
import { CampaignDetailsCreatorsService } from './campaign-details-creators.service';
import { CampaignDetailsCreatorsStore } from './campaign-details-creators.store';

@Component({
  imports: [
    CampaignDetailsCreatorsGridComponent,
    CampaignDetailsCreatorsTableComponent,
    CampaignDetailsDeliverablesFilterSelectComponent,
    IconComponent,
    MatButtonModule,
    TranslateModule,
  ],
  providers: [CampaignDetailsCreatorsStore],
  selector: 'app-campaign-details-creators',
  styleUrl: './campaign-details-creators.component.scss',
  templateUrl: './campaign-details-creators.component.html',
})
export class CampaignDetailsCreatorsComponent {
  public readonly campaign = input.required<TCampaignApi | null>();
  public readonly ctaKpiStatusData = input<ICreatorCtaKpiStatusApi[] | null>(null);
  public readonly campaignAnalyticsApi = input<ICampaignAnalyticsApi | null>(null);
  public readonly analyticsNodes = input<IAnalyticsNode[] | null>(null);
  public readonly selectedPlatformFeatures = input<Set<EPlatformFeature>>(new Set());
  public readonly selectedCreatorIds = input<Set<string>>(new Set());
  public readonly loading = input<boolean>(false);
  public readonly creatorCampaignExports = input<Map<string, ICreatorCampaignExport>>(new Map());
  public readonly campaignExport = input<ICampaignExportApi | null>(null);
  public readonly creatorBriefingMode = input<boolean>(false);
  public readonly exportMode = input<boolean>(false);

  public readonly deepLinkClicked = output<TDeepLinkEvent>();

  public readonly viewType = linkedSignal<string | null, 'grid' | 'list'>({
    computation: (id) => {
      if (!id) {
        return 'grid';
      }

      const stored = localStorage.getItem(`creators-view-type-${id}`);

      return stored === 'list' ? 'list' : 'grid';
    },
    source: () => this.campaign()?.id ?? (this.route.snapshot.params['id'] as string | undefined) ?? null,
  });

  public readonly invitationFilter = signal<Set<string>>(new Set());
  public readonly briefingFilter = signal<Set<string>>(new Set());
  public readonly statusFilter = signal<Set<string>>(new Set());

  public readonly creatorsData = computed<ICampaignDetailsCreator[] | null>(() => {
    const campaign = this.campaign();
    const ctaKpiStatusData = this.ctaKpiStatusData();

    if (!campaign || !ctaKpiStatusData) {
      return null;
    }

    return this.creatorsService.buildCampaignDetailsCreators(
      campaign,
      ctaKpiStatusData,
      this.campaignAnalyticsApi(),
      this.analyticsNodes(),
    );
  });

  public readonly creators = computed(() => {
    return [...(this.creatorsData() ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  });

  public readonly invitedCreatorIds = computed(() => {
    return new Set(
      this.store
        .invitations()
        .filter((inv) => !!inv.createdUserId)
        .map((inv) => inv.creatorId),
    );
  });

  public readonly invitationOptions = computed((): ICampaignDetailsDeliverablesFilterOption<string>[] => {
    const creators = this.creators();
    const invitedIds = this.invitedCreatorIds();
    const options: ICampaignDetailsDeliverablesFilterOption<string>[] = [];

    if (creators.some((c) => invitedIds.has(c.id))) {
      options.push({
        label: this.translate.instant('pages.campaignDetails.creators.filters.invited'),
        value: 'invited',
      });
    }

    if (creators.some((c) => !invitedIds.has(c.id))) {
      options.push({
        label: this.translate.instant('pages.campaignDetails.creators.filters.uninvited'),
        value: 'uninvited',
      });
    }

    return options;
  });

  public readonly briefingOptions = computed((): ICampaignDetailsDeliverablesFilterOption<string>[] => {
    const creators = this.creators();
    const exports = this.creatorCampaignExports();
    const options: ICampaignDetailsDeliverablesFilterOption<string>[] = [];

    if (creators.some((c) => exports.get(c.id)?.isActive)) {
      options.push({
        label: this.translate.instant('pages.campaignDetails.creators.filters.briefed'),
        value: 'briefed',
      });
    }

    if (creators.some((c) => !exports.get(c.id)?.isActive)) {
      options.push({
        label: this.translate.instant('pages.campaignDetails.creators.filters.unbriefed'),
        value: 'unbriefed',
      });
    }

    return options;
  });

  public readonly statusOptions = computed((): ICampaignDetailsDeliverablesFilterOption<string>[] => {
    const creators = this.creators();
    const statusMap: Partial<Record<EAnalyticsStatus, string>> = {
      [EAnalyticsStatus.ok]: 'pages.campaignDetails.creators.filters.allGood',
      [EAnalyticsStatus.problem]: 'pages.campaignDetails.creators.filters.attention',
      [EAnalyticsStatus.failed]: 'pages.campaignDetails.creators.filters.critical',
      [EAnalyticsStatus.processing]: 'pages.campaignDetails.creators.filters.notStarted',
    };

    return (Object.entries(statusMap) as [EAnalyticsStatus, string][])
      .filter(([status]) => creators.some((c) => c.mergedStatus === status))
      .map(([status, labelKey]) => ({
        label: this.translate.instant(labelKey),
        value: status,
      }));
  });

  public readonly invitationFilterEmptyLabel = computed(() => {
    return this.translate.instant('pages.campaignDetails.creators.filters.allInvitations', {
      count: this.invitationOptions().length,
    });
  });

  public readonly briefingFilterEmptyLabel = computed(() => {
    return this.translate.instant('pages.campaignDetails.creators.filters.allBriefings', {
      count: this.briefingOptions().length,
    });
  });

  public readonly statusFilterEmptyLabel = computed(() => {
    return this.translate.instant('pages.campaignDetails.creators.filters.allStatuses', {
      count: this.statusOptions().length,
    });
  });

  private readonly filteredByFilters = computed(() => {
    const invFilter = this.invitationFilter();
    const briefFilter = this.briefingFilter();
    const statFilter = this.statusFilter();
    const creatorIds = this.selectedCreatorIds();

    return this.creators().filter((creator) => {
      if (creatorIds.size > 0 && !creatorIds.has(creator.id)) return false;

      if (invFilter.size > 0) {
        const isInvited = this.invitedCreatorIds().has(creator.id);

        if (!invFilter.has(isInvited ? 'invited' : 'uninvited')) return false;
      }

      if (briefFilter.size > 0) {
        const isBriefed = !!this.creatorCampaignExports().get(creator.id)?.isActive;

        if (!briefFilter.has(isBriefed ? 'briefed' : 'unbriefed')) return false;
      }

      if (statFilter.size > 0) {
        if (!statFilter.has(creator.mergedStatus)) return false;
      }

      return true;
    });
  });

  public readonly displayCreators = computed(() => {
    const selectedPlatformFeatures = Array.from(this.selectedPlatformFeatures());

    if (selectedPlatformFeatures.length === 0) {
      return this.filteredByFilters();
    }

    return this.filteredByFilters().map((creator) => ({
      ...creator,
      usedPlatforms: creator.usedPlatforms.filter((usedPlatform) =>
        selectedPlatformFeatures.some((platformFeature) =>
          SocialUtilitiesService.arePlatformsEqual(usedPlatform.platform, platformFeature),
        ),
      ),
    }));
  });

  private readonly creatorsService = inject(CampaignDetailsCreatorsService);
  private readonly eventTrackingService = inject(EventTrackingService);
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject(CampaignDetailsCreatorsStore);
  private readonly translate = inject(TranslateService);

  constructor() {
    effect(() => {
      if (!this.exportMode()) {
        this.store.fetchInvitations();
      }
    });
  }

  public onToggleView(type: 'grid' | 'list'): void {
    this.viewType.set(type);
    const campaignId = this.campaign()?.id;

    if (campaignId) {
      localStorage.setItem(`creators-view-type-${campaignId}`, type);
    }

    this.eventTrackingService.track(EEventType.campaignAnalyticsViewModeChanged, {
      campaignId,
      currentTab: toEventFrontendTab(ECampaignDetailsTab.creators),
      viewMode: toEventViewMode(type),
    });
  }

  public onDeepLinkClicked(event: TDeepLinkEvent): void {
    this.deepLinkClicked.emit(event);
  }
}
